import * as React from 'react';
// Import hooks.
import {
	useInputAddressLocation,
	UseInputAddressLocationParameters,
	UseInputAddressLocationResults,
} from '@app/hooks/view/input';
// Import props.
import {
	FormPopupAddressVerificationProps,
	FormPopupSingleLineAddressVerificationProps,
} from '@app/props';
// Import api models.
import {
	Address,
	AddressVerification,
    PossibleState,
} from '@app/models/api';

interface UseAddressVerificationParameters {
	addressLocationInputParameters: UseInputAddressLocationParameters;
	possibleStates: PossibleState[];
	ignorePossibleStateRiskAllowed: Boolean;
	initialInput?: AddressVerification;
	verifySingleLineAddress: (singleLineAddress: string) => Promise<AddressVerification>;
	verifyAddress: (address: Address) => Promise<AddressVerification>;
	finishConfirmAddressVerification: (address: Address) => void
}

interface UseAddressVerificationResults {
	addressLocationInput: UseInputAddressLocationResults,
	setVerifiedAddressResult: (newVerifiedAddressResult: AddressVerification | undefined) => void;
	applyFormPopupSingleLineAddressVerificationProps: () => FormPopupSingleLineAddressVerificationProps;
	applyFormPopupAddressVerificationProps: () => FormPopupAddressVerificationProps;
	verifyAddressAsSingleLineAsync: (singleLineAddress: string) => Promise<void>;
	verifyAddressAsComponentsAsync: (address: Address) => Promise<void>;
	isSingleLineAddressVerificationRequired: () => boolean;
	isAddressVerificationRequired: () => boolean;
}

const useAddressVerification = (params: UseAddressVerificationParameters): UseAddressVerificationResults => {

	// #region exported methods

	/**
	 * Verifies the address as a single line input.
	 * @param singleLineAddress
	 */
	const verifyAddressAsSingleLineAsync = async (singleLineAddress: string): Promise<void> => {
		// Verify address as single line input.
		let addressVerificationResult: AddressVerification = await params.verifySingleLineAddress(singleLineAddress);
		// Attempt to save the result if it's a match.
		finishOrStoreAddressVerificationResult(addressVerificationResult);
	}

	/**
	 * Verifies the address as a componentized input.
	 * @param address
	 */
	const verifyAddressAsComponentsAsync = async (address: Address): Promise<void> => {
		// Verify address as components.
		let addressVerificationResult: AddressVerification = await params.verifyAddress(address);
		// Attempt to save the result, if it is a match.
		finishOrStoreAddressVerificationResult(addressVerificationResult);
	}

	/**
	 * Checks to see if a single line address verification is required. 
	 **/
	const isSingleLineAddressVerificationRequired = (): boolean => {
		return isVerifiedAddressNotMatch()
			&& !String.isNullOrWhiteSpace(addressLocationInput.value.addressSingleLine)
			&& addressLocationInput.value.address === undefined;
	}

	/**
	 * Checks to see if a comparative address verification is required. 
	 **/
	const isAddressVerificationRequired = (): boolean => {
		return isVerifiedAddressNotMatch()
			&& String.isNullOrWhiteSpace(addressLocationInput.value.addressSingleLine)
			&& addressLocationInput.value.address !== undefined;
	}

	/**
	 * Returns props for form popup single line address verification. 
	 **/
	const applyFormPopupSingleLineAddressVerificationProps = (): FormPopupSingleLineAddressVerificationProps => {
		// Apply the single line address verification form props.
		return {
			isOpen: isSingleLineAddressVerificationRequired(),
			possibleStates: params.possibleStates,
			ignorePossibleStateRiskAllowed: params.ignorePossibleStateRiskAllowed,
			verifiedAddress: verifiedAddressResult?.addressComponents ?? new Address(),
			onSaveAddress: confirmSingleLineAddressUserVerificationAsync,
			onGetAddressSuggestions: params.addressLocationInputParameters.addressInputParameters.onGetAddressSuggestions,
			onClose: cancelAddressVerification,
		}
	};

	/**
	 * Returns props for form popup address verification. 
	 **/
	const applyFormPopupAddressVerificationProps = (): FormPopupAddressVerificationProps => {
		// Apply the address verification form props.
		return {
			isOpen: isAddressVerificationRequired(),
			verifiedAddress: verifiedAddressResult?.addressComponents ?? new Address(),
			enteredAddress: addressLocationInput.value.address ?? new Address(),
			onSaveAddress: confirmComponentizedAddressUserVerificationAsync,
			onClose: cancelAddressVerification,
		}
	};

	/**
	 * Updates the address verification result.
	 * @param newVerifiedAddressResult
	 */
	const setVerifiedAddressResult = (newVerifiedAddressResult: AddressVerification | undefined): void => {
		_setVerifiedAddressResult(newVerifiedAddressResult);
	}

	// #endregion exported methods


	// #region private methods

	/**
	 *  Confirm the single line address as a user verification.
	 **/
	const confirmSingleLineAddressUserVerificationAsync = async (userVerifiedAddress: Address): Promise<void> => {
		// Clear any address verification results.
		setVerifiedAddressResult(undefined);
		// Set the user verified address result.
		addressLocationInput.setAddress(userVerifiedAddress);
		// After verifying the single line address result, attempt to match it in componentized format.
		let addressVerificationResult: AddressVerification = await params.verifyAddress(userVerifiedAddress);
		finishOrStoreAddressVerificationResult(addressVerificationResult);
	}

	/**
	 *  Confirm the address as a user verification.
	 **/
	const confirmComponentizedAddressUserVerificationAsync = async (verifiedAddress: Address): Promise<void> => {
		// Clear any address verification results.
		setVerifiedAddressResult(undefined);
		// Set the user verified address result.
		addressLocationInput.setAddress(verifiedAddress);
		// After confirming the address verification, finish the verification process.
		params.finishConfirmAddressVerification(verifiedAddress);
	}

	/**
	 * Saves or finishes the address verification record.
	 * @param addressVerification
	 */
	const finishOrStoreAddressVerificationResult = (addressVerification: AddressVerification): void => {
		if (addressVerification.isMatch
			&& addressVerification.addressComponents !== null) {
			// If the results are a match, then proceed forward to finish the verification process.
			params.finishConfirmAddressVerification(addressVerification.addressComponents);
		}
		else {
			// If the results are not a match, then save the result to be later verified.
			setVerifiedAddressResult(addressVerification);
		}
	}

	/**
	 * Checks to see if the verified address is result is not a match. 
	 **/
	const isVerifiedAddressNotMatch = (): boolean => {
		return verifiedAddressResult !== undefined
			&& verifiedAddressResult.addressComponents !== null
			&& !verifiedAddressResult.isMatch;
	}

	/**
	 *  Resets the address verification results to undefined.
	 **/
	const cancelAddressVerification = (): void => {
		setVerifiedAddressResult(undefined);
	}

	// #endregion private methods


	// #region variables

	// Set the initial address verification state.
	const [verifiedAddressResult, _setVerifiedAddressResult] = React.useState<AddressVerification | undefined>(params.initialInput)

	// Instantiate the 'address' input hook.
	const addressLocationInput: UseInputAddressLocationResults = useInputAddressLocation({
		...params.addressLocationInputParameters,
	});

	// #endregion variables


	return {
		addressLocationInput,
		setVerifiedAddressResult,
		applyFormPopupSingleLineAddressVerificationProps,
		applyFormPopupAddressVerificationProps,
		verifyAddressAsSingleLineAsync,
		verifyAddressAsComponentsAsync,
		isSingleLineAddressVerificationRequired,
		isAddressVerificationRequired,
	};
}

// Export parameters.
export {
	UseAddressVerificationParameters,
}

// Export results.
export {
	UseAddressVerificationResults,
}

export default useAddressVerification;